import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FlaskConical, Send, Loader2, Search, Inbox, CheckCheck, Reply, Wand2, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  XbPage, WithScheme, SchemeHeading, Card, C, CodeBlock, Notice, errorText,
} from './common';
import xbApi from '../../services/xbApi';
import { countryName } from '../../utils/crossBorder';
import {
  creditSample, statusRequestSample, answerSample, readMessage, describeMessage, firstText, uetr as newUetr,
  isMontran, prettyXml,
} from '../../utils/xbSamples';

/** The response headers worth showing, in the order a reader wants them. */
const SHOWN_HEADERS = [
  'x-papssrtp-reqsts', 'x-papssrtp-messageseq', 'x-papssrtp-messagetype', 'x-papssrtp-remainingoutputs',
  'x-papssrtp-possibleduplicate', 'x-incoming-case', 'x-incoming-note', 'x-scheme-profile', 'x-profile-refusal',
  'x-profile-findings', 'x-fx-quote', 'x-liquidity', 'x-settlement', 'x-screening', 'x-cert-replay',
];

/**
 * Ready-made messages in the chosen scheme's format, to sign and send from
 * the portal and read the answers: a payment (carrying an accepted FX quote
 * when one is handed over), a status request, and, for a scheme whose
 * participants poll, the inbox: poll, confirm, and answer a payment.
 */
export default function XbSamples() {
  return (
    <XbPage icon={FlaskConical} title="Message samples" wide
      intro="Messages that meet the scheme's rules, ready to sign with your key in the Sandbox and send. Edit them to see what the scheme refuses.">
      <WithScheme>{(scheme) => <Samples key={scheme.profileId} scheme={scheme} />}</WithScheme>
    </XbPage>
  );
}

/** Signs as the participant, then sends: each step's answer, or where it stopped. */
async function signAndSend(scheme, xml, send) {
  const montran = isMontran(scheme.profile);
  const signed = await xbApi.sign(scheme.profileId, xml, montran);
  if (signed.status !== 200) return { stage: 'sign', response: signed };
  const response = await send(signed.body);
  return { stage: 'send', signed: signed.body, response };
}

function Samples({ scheme }) {
  const s = scheme.profile;
  const polls = s.offers?.polls;
  const location = useLocation();
  // A quote handed over from FX Quotes belongs to the scheme it was given under ("sbx-xb@0.1.0").
  const handed = location.state?.quote || null;
  const handedQuote = handed && String(handed.profile || '').startsWith(`${scheme.profileId}@`) ? handed : null;
  const [last, setLast] = useState(null);   // { msgId, endToEndId } of the last payment built

  return (
    <>
      <SchemeHeading summary={s} />
      <PaymentSample scheme={scheme} quote={handedQuote} onBuilt={setLast} />
      <StatusSample scheme={scheme} last={last} />
      {polls ? <InboxTool scheme={scheme} /> : <AnswerTool scheme={scheme} />}
    </>
  );
}

// ── A payment ──────────────────────────────────────────────────────────────

function PaymentSample({ scheme, quote, onBuilt }) {
  const s = scheme.profile;
  const corridors = (s.corridors || []).filter((c) => c.from === scheme.homeCountry && c.send === scheme.homeCurrency);
  const quotedTo = quote && corridors.find((c) => c.receive === quote.receive?.currency)?.to;
  const [to, setTo] = useState(quotedTo || corridors[0]?.to || '');
  const [amount, setAmount] = useState(quote?.send?.amount || '100000');
  const [scenario, setScenario] = useState('');
  const [xml, setXml] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const corridor = corridors.find((c) => c.to === to);
  const needsQuote = s.offers?.quotes;

  const build = useCallback(() => {
    if (!corridor) return;
    const fixedReceive = quote?.basis === 'FIXED_RECEIVE';
    const doc = prettyXml(creditSample({
      summary: s, from: scheme.homeCountry, to, memberId: scheme.memberId,
      uetr: quote?.uetr || newUetr(),
      send: quote ? quote.send : { amount: amount.trim(), currency: corridor.send },
      instructed: fixedReceive ? quote.receive : undefined,
      xchgRate: fixedReceive ? quote.xchgRate : undefined,
    }));
    setXml(doc); setResult(null);
    onBuilt({ msgId: firstText(doc, 'MsgId'), endToEndId: firstText(doc, 'EndToEndId') });
  }, [corridor, quote, s, scheme.homeCountry, scheme.memberId, to, amount, onBuilt]);

  // Build one straight away, so the page opens on a message.
  useEffect(() => { if (!xml) build(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      setResult(await signAndSend(scheme, xml,
        (signed) => xbApi.sendCredit(scheme.profileId, signed, s.offers?.polls, scenario || undefined)));
    } catch (e) {
      setResult({ stage: 'send', error: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`A payment (${s.messages['pacs.008']})`} icon={Send}>
      {quote ? (
        <Notice tone="ok" title={`Carrying quote ${quote.quoteId}`}>
          {quote.send.amount} {quote.send.currency} for {quote.receive.amount} {quote.receive.currency}, UETR
          {' '}<C>{quote.uetr}</C>. Send it before the quote expires.
        </Notice>
      ) : needsQuote && (
        <Notice tone="warn" title="This scheme needs an accepted quote for each payment">
          Without one the payment is refused. <Link to="/xb/quotes" className="underline">Get a quote</Link> and use
          its <em>Send the payment</em> button, or send this one to see the refusal.
        </Notice>
      )}
      <div className="grid gap-3 sm:grid-cols-4 mt-4">
        <label className="block text-xs font-medium text-gray-700">
          To
          <select value={to} onChange={(e) => setTo(e.target.value)} disabled={Boolean(quote)}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50">
            {corridors.map((c) => <option key={c.to} value={c.to}>{countryName(c.to)} ({c.receive})</option>)}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Amount in {corridor?.send}
          <input value={amount} onChange={(e) => setAmount(e.target.value)} disabled={Boolean(quote)} inputMode="decimal"
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono disabled:bg-gray-50" />
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Force the answer <span className="font-normal text-gray-500">(test)</span>
          <select value={scenario} onChange={(e) => setScenario(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">No: the scheme decides</option>
            <option value="REJECT">Refuse it</option>
            <option value="REJECT:AM04">Refuse it: AM04</option>
          </select>
        </label>
        <div className="flex items-end">
          <button type="button" onClick={build}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            <Wand2 className="w-4 h-4" aria-hidden="true" /> New message
          </button>
        </div>
      </div>
      <Editor xml={xml} onChange={setXml} />
      <SendButton busy={busy} disabled={!xml.trim()} onClick={send}>Sign and send</SendButton>
      <Result result={result} />
    </Card>
  );
}

// ── A status request ───────────────────────────────────────────────────────

function StatusSample({ scheme, last }) {
  const s = scheme.profile;
  const [msgId, setMsgId] = useState('');
  const [e2e, setE2e] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (last) { setMsgId(last.msgId || ''); setE2e(last.endToEndId || ''); } }, [last]);

  const xml = useMemo(() => (e2e.trim() ? statusRequestSample({
    summary: s, orgnlMsgId: msgId.trim() || null, orgnlEndToEndId: e2e.trim(), memberId: scheme.memberId,
  }) : ''), [s, msgId, e2e, scheme.memberId]);

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      setResult(await signAndSend(scheme, xml,
        (signed) => xbApi.sendStatusRequest(scheme.profileId, signed, s.offers?.polls)));
    } catch (e) {
      setResult({ stage: 'send', error: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`Ask after a payment (${s.messages['pacs.028']})`} icon={Search}>
      <p className="text-sm text-gray-600">The scheme answers with the payment's stored status, or <C>NOOR</C> when it
        never received it: then it is safe to send again. The fields hold the last payment built above.</p>
      <div className="grid gap-3 sm:grid-cols-2 mt-3">
        <label className="block text-xs font-medium text-gray-700">
          Its message id
          <input value={msgId} onChange={(e) => setMsgId(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
        </label>
        <label className="block text-xs font-medium text-gray-700">
          Its end-to-end id
          <input value={e2e} onChange={(e) => setE2e(e.target.value)}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
        </label>
      </div>
      <SendButton busy={busy} disabled={!xml} onClick={send}>Sign and send</SendButton>
      <Result result={result} />
    </Card>
  );
}

// ── The inbox (a scheme whose participants poll) ───────────────────────────

function InboxTool({ scheme }) {
  const s = scheme.profile;
  const [polled, setPolled] = useState(null);   // { status, headers, body }
  const [ackResult, setAckResult] = useState(null);
  const [answer, setAnswer] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const message = polled?.body ? readMessage(polled.body) : null;
  const seq = polled?.headers?.['x-papssrtp-messageseq'];
  const type = polled?.headers?.['x-papssrtp-messagetype'];
  const isPayment = type === 'pacs.008';
  const refusal = s.reasons?.MISSING_ORIGINATOR_DATA;

  const poll = async () => {
    setBusy(true); setError(null); setAckResult(null); setAnswer(null);
    try { setPolled(await xbApi.poll(scheme.profileId)); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const ack = async () => {
    setBusy(true); setError(null);
    try { setAckResult(await xbApi.ack(scheme.profileId, seq)); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const reply = async (status, reason) => {
    setBusy(true); setError(null); setAnswer(null);
    try {
      const xml = answerSample({ summary: s, memberId: scheme.memberId, status, reason,
        payment: { msgId: message.msgId, msgNmId: message.type } });
      setAnswer(await signAndSend(scheme, xml, (signed) => xbApi.sendAnswer(scheme.profileId, signed, true)));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Your inbox" icon={Inbox}>
      <p className="text-sm text-gray-600">What your system would do: take the next message, then confirm it, or, for a
        payment to you, answer it. Polling here takes the message as your system would: once confirmed, it is gone.</p>
      <div className="mt-3">
        <SendButton busy={busy} onClick={poll} icon={Inbox}>Poll the inbox</SendButton>
      </div>
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
      {polled && (
        <div className="mt-4 space-y-3">
          {!polled.body ? (
            <p className="text-sm text-gray-600">Empty: nothing waits for you.</p>
          ) : (
            <>
              <p className="text-sm text-gray-800">
                <span className="font-medium">Message {seq}</span> ({type}
                {polled.headers['x-papssrtp-possibleduplicate'] === 'true' ? ', handed out before' : ''};
                {' '}{polled.headers['x-papssrtp-remainingoutputs'] || 0} more waiting): {describeMessage(message)}
                {message?.info ? <> · <span className="text-gray-600">{message.info}</span></> : null}
              </p>
              <Collapsible label="The message as polled"><CodeBlock>{polled.body}</CodeBlock></Collapsible>
              {isPayment ? (
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">A payment to you: answer within {s.replies.responseSeconds} s. Your
                    answer is its receipt, so it is not confirmed with MessageAck.</p>
                  <div className="flex gap-2 flex-wrap">
                    <SendButton busy={busy} onClick={() => reply(s.replies.incomingReply)} icon={Reply}>
                      Accept ({s.replies.incomingReply})</SendButton>
                    <SendButton busy={busy} onClick={() => reply('RJCT', 'AC01')} icon={Reply} secondary>
                      Refuse: AC01, no such account</SendButton>
                    {refusal && <SendButton busy={busy} onClick={() => reply('RJCT', refusal)} icon={Reply} secondary>
                      Refuse: {refusal}, originator data missing</SendButton>}
                  </div>
                  <Result result={answer} />
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <SendButton busy={busy} onClick={ack} icon={CheckCheck} secondary>Confirm (MessageAck {seq})</SendButton>
                  {ackResult && <span className="text-sm text-gray-800">HTTP {ackResult.status}: <C>{ackResult.body}</C></span>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Answering a delivered payment (a scheme that pushes) ───────────────────

function AnswerTool({ scheme }) {
  const s = scheme.profile;
  const [cases, setCases] = useState(null);
  const [chosen, setChosen] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    xbApi.incoming(scheme.profileId)
      .then((list) => {
        const open = (list || []).filter((c) => c.state === 'AWAITING' || c.state === 'HELD');
        setCases(open);
        setChosen((cur) => (open.some((c) => String(c.id) === cur) ? cur : String(open[0]?.id || '')));
      })
      .catch(() => setCases([]));
  }, [scheme.profileId]);
  useEffect(() => { load(); }, [load]);

  const c = (cases || []).find((x) => String(x.id) === chosen);
  const reply = async (status, reason) => {
    setBusy(true); setResult(null);
    try {
      const xml = answerSample({ summary: s, status, reason,
        payment: { msgId: c.msgId, endToEndId: c.endToEndId, uetr: c.uetr } });
      setResult(await signAndSend(scheme, xml, (signed) => xbApi.sendAnswer(scheme.profileId, signed, false)));
      load();
    } catch (e) {
      setResult({ stage: 'send', error: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`Answer a payment sent to you (${s.messages['pacs.002']})`} icon={Reply}>
      <p className="text-sm text-gray-600">For a payment your callback took but did not answer in its response. Send yourself
        one from <Link to="/xb/incoming" className="underline">Incoming Payments</Link>.</p>
      {cases && cases.length === 0 && <p className="text-sm text-gray-500 mt-3">No payment waits on your answer.</p>}
      {c && (
        <div className="mt-3 space-y-3">
          <label className="block text-xs font-medium text-gray-700 max-w-md">
            Payment
            <select value={chosen} onChange={(e) => setChosen(e.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
              {cases.map((x) => <option key={x.id} value={x.id}>#{x.id} {x.scenario}: expects {x.expected}</option>)}
            </select>
          </label>
          <div className="flex gap-2 flex-wrap">
            <SendButton busy={busy} onClick={() => reply(s.replies.incomingReply)} icon={Reply}>Accept ({s.replies.incomingReply})</SendButton>
            {s.offers?.holds && c.state === 'AWAITING' && <SendButton busy={busy} onClick={() => reply('PDNG')} icon={Reply} secondary>Hold (PDNG)</SendButton>}
            <SendButton busy={busy} onClick={() => reply('RJCT', 'AC01')} icon={Reply} secondary>Refuse: AC01</SendButton>
            {s.reasons?.MISSING_ORIGINATOR_DATA && <SendButton busy={busy} onClick={() => reply('RJCT', s.reasons.MISSING_ORIGINATOR_DATA)} icon={Reply} secondary>
              Refuse: {s.reasons.MISSING_ORIGINATOR_DATA}</SendButton>}
            {s.reasons?.REGULATORY && <SendButton busy={busy} onClick={() => reply('RJCT', s.reasons.REGULATORY)} icon={Reply} secondary>
              Refuse: {s.reasons.REGULATORY}</SendButton>}
          </div>
        </div>
      )}
      <Result result={result} />
    </Card>
  );
}

// ── Pieces ─────────────────────────────────────────────────────────────────

function Editor({ xml, onChange }) {
  return (
    <label className="block mt-4 text-xs font-medium text-gray-700">
      The message, unsigned <span className="font-normal text-gray-500">(edit it freely)</span>
      <textarea value={xml} onChange={(e) => onChange(e.target.value)} spellCheck={false} rows={16}
        className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono leading-relaxed" />
    </label>
  );
}

function SendButton({ busy, disabled, onClick, icon: Icon = Send, secondary = false, children }) {
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-40 ${secondary
        ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        : 'bg-primary-600 text-white hover:bg-primary-700'} ${secondary ? '' : 'mt-3'}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Icon className="w-4 h-4" aria-hidden="true" />}
      {children}
    </button>
  );
}

function Collapsible({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline">
        {open ? <ChevronDown className="w-4 h-4" aria-hidden="true" /> : <ChevronRight className="w-4 h-4" aria-hidden="true" />} {label}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/** A JSON problem's message, else the body as it came. */
function problemText(body) {
  try { const j = JSON.parse(body); return j.message || j.error || body; } catch { return body; }
}

/** What happened: where it stopped, the HTTP status, the headers that matter, and the answer. */
function Result({ result }) {
  if (!result) return null;
  if (result.error) return <p className="text-sm text-red-600 mt-3">{result.error}</p>;
  const r = result.response;
  if (result.stage === 'sign') {
    return <p className="text-sm text-red-600 mt-3">Not signed (HTTP {r.status}): {problemText(r.body)}</p>;
  }
  const isXml = (r.body || '').trim().startsWith('<');
  const message = isXml ? readMessage(r.body) : null;
  let json = null;
  if (!isXml && r.body) { try { json = JSON.parse(r.body); } catch { /* not JSON */ } }
  const headers = SHOWN_HEADERS.filter((h) => r.headers?.[h] != null);
  const ok = r.status >= 200 && r.status < 300;

  return (
    <div className="mt-4 space-y-2 border-t border-gray-100 pt-4" aria-live="polite">
      <p className={`text-sm font-medium ${ok ? 'text-gray-900' : 'text-red-700'}`}>
        HTTP {r.status}{message ? <> · {describeMessage(message)}</> : null}
        {json?.note ? <> · {json.note}</> : null}
        {!ok && json?.message ? <> · {json.message}</> : null}
      </p>
      {message?.info && <p className="text-sm text-gray-700">{message.info}</p>}
      {json?.incoming && (
        <p className="text-sm text-gray-700">Case #{json.incoming.id}: {json.incoming.state}
          {json.incoming.verdict ? ` · ${json.incoming.verdict}` : ''}</p>
      )}
      {headers.length > 0 && (
        <dl className="text-xs font-mono bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-0.5">
          {headers.map((h) => <div key={h}><dt className="inline text-gray-500">{h}: </dt><dd className="inline text-gray-900">{r.headers[h]}</dd></div>)}
        </dl>
      )}
      {!r.body && ok && <p className="text-sm text-gray-600">No body: a receipt. The answer waits in your inbox.</p>}
      {isXml && <Collapsible label="The answer"><CodeBlock>{r.body}</CodeBlock></Collapsible>}
      {result.signed && <Collapsible label="What was sent, signed"><CodeBlock>{result.signed}</CodeBlock></Collapsible>}
    </div>
  );
}
